import React from 'react';
import { motion } from 'motion/react';
import { Sparkles, ArrowRight, CheckCircle2, Zap } from 'lucide-react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';

const AIPostGenerator = () => {
  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title="AI Social Media Post Generator - Create Content Fast"
        description="PlataPost's AI social media post generator creates high-quality, platform-specific content in seconds. Perfect for Instagram, LinkedIn, and more."
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-40">
          <div className="flex-1 space-y-8">
            <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full glass-card border border-white/5 text-indigo-400 text-[10px] font-black uppercase tracking-widest leading-none">
              <Zap className="w-3.5 h-3.5" />
              <span>Instant Creation</span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">AI Social Media <br /> <span className="text-indigo-500">Post Generator.</span></h1>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed font-medium">
              Stop wasting hours on content creation. Our AI generator creates high-performing posts tailored to your brand voice and target platforms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/dashboard" className="px-10 py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
                Generate My First Post
              </Link>
            </div>
          </div>
          <div className="flex-1 w-full glass-card rounded-[4rem] p-12 relative overflow-hidden border border-white/5 shadow-2xl shadow-indigo-600/5">
            <div className="absolute inset-0 bg-indigo-600/5 blur-[100px] pointer-events-none" />
            <div className="absolute -top-10 -right-10 opacity-20">
              <Sparkles className="w-64 h-64 text-indigo-600 blur-3xl animate-pulse" />
            </div>
            <div className="relative space-y-6">
               <div className="p-6 bg-white/5 rounded-3xl border border-white/10 group-hover:scale-105 transition-transform duration-500">
                 <div className="h-4 w-1/2 bg-white/10 rounded-full mb-4 animate-pulse" />
                 <div className="h-4 w-full bg-white/5 rounded-full" />
               </div>
               <div className="p-6 bg-white/[0.02] rounded-3xl border border-white/5">
                 <div className="h-4 w-3/4 bg-white/10 rounded-full mb-4" />
                 <div className="h-4 w-full bg-white/5 rounded-full" />
               </div>
               <div className="p-6 bg-indigo-500/10 rounded-3xl border border-indigo-500/20 shadow-xl shadow-indigo-500/5">
                 <div className="h-4 w-1/3 bg-indigo-400/20 rounded-full mb-4" />
                 <div className="h-20 w-full bg-indigo-400/5 rounded-2xl border border-indigo-400/10" />
               </div>
            </div>
          </div>
        </div>

        <section className="mb-40">
           <div className="text-center mb-24 space-y-4">
             <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter leading-tight">How It Works</h2>
             <p className="text-gray-400 font-medium max-w-xl mx-auto">From idea to publish-ready content in three simple steps.</p>
           </div>
           <div className="grid md:grid-cols-3 gap-12">
             {[
               { title: "Input Your Topic", desc: "Just enter a few keywords or a link to your latest project." },
               { title: "AI Analysis", desc: "Our engine analyzes your brand voice and platform best practices." },
               { title: "Instant Generation", desc: "Get multiple variations of posts with copy, hashtags, and visual ideas." }
             ].map((step, i) => (
               <div key={i} className="text-center p-10 glass-card rounded-[2.5rem] border border-white/5 group relative">
                 <div className="w-16 h-16 bg-indigo-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-10 font-black text-2xl shadow-xl shadow-indigo-600/20 group-hover:scale-110 transition-transform">
                   {i+1}
                 </div>
                 <h3 className="font-black text-white text-2xl mb-6 tracking-tight">{step.title}</h3>
                 <p className="text-gray-400 font-medium leading-relaxed">{step.desc}</p>
               </div>
             ))}
           </div>
        </section>

        <section className="glass-card rounded-[4rem] p-12 lg:p-24 border border-white/5 mb-40 relative overflow-hidden shadow-2xl shadow-indigo-600/5">
          <div className="absolute inset-0 bg-indigo-600/5 blur-[120px] pointer-events-none" />
          <div className="max-w-3xl mx-auto relative z-10 text-center">
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-20 tracking-tighter leading-tight">Frequently Asked Questions</h2>
            <div className="space-y-6 text-left">
              {[
                { q: "Is the content original?", a: "Yes, every generation is unique to your prompt and brand data." },
                { q: "Which platforms are supported?", a: "Instagram, LinkedIn, TikTok, Facebook, and Twitter." },
                { q: "Does it help with hashtags?", a: "Absolutely. We generate relevant, high-performing hashtags for every platform." }
              ].map((faq, i) => (
                <div key={i} className="bg-white/5 p-8 rounded-3xl border border-white/10 hover:bg-white/10 transition-all group">
                  <h4 className="font-black text-white text-lg mb-4 tracking-tight group-hover:text-indigo-400 transition-colors">{faq.q}</h4>
                  <p className="text-gray-400 font-medium leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="text-center">
          <Link to="/dashboard" className="inline-flex items-center gap-4 px-12 py-6 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-2xl shadow-indigo-600/30">
            Try the AI Post Generator Free
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AIPostGenerator;
